import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Heart, MessageCircle, Share2 } from 'lucide-react';

/**
 * LiveAdPreview Component
 * 
 * A reusable component that displays a Facebook-style ad preview
 * 
 * @param {Object} props - Component props
 * @param {string} props.campaignName - Name of the campaign to display in the ad header
 * @param {string} props.adHeadline - Main headline text for the ad
 * @param {string} props.bodyCopy - Main body text/description for the ad
 * @param {string} props.ctaType - Call-to-action button text
 * @param {Array} props.creativeFiles - Array of file objects to preview (optional)
 * @param {string} props.title - Title for the preview card (default: "LIVE AD PREVIEW")
 * @param {boolean} props.sticky - Whether the preview should stick to the top (default: true)
 * @param {string} props.className - Additional CSS classes for the container
 */
const LiveAdPreview = ({
  campaignName = 'Campaign Name',
  adHeadline = 'Experience the Sunset',
  bodyCopy = 'Join us for an unforgettable evening with Corona. Limited time offer at participating locations.',
  ctaType = 'Learn More',
  creativeFiles = [],
  title = 'LIVE AD PREVIEW',
  sticky = true,
  className = ''
}) => {
  // Get the first creative file for preview
  const previewCreative = creativeFiles && creativeFiles.length > 0 ? creativeFiles[0] : null;
  
  // Determine the image source - could be a File object or a URL string
  const getImageSrc = () => {
    if (!previewCreative) return null;
    
    // If it's an object with a url property (from API)
    if (previewCreative.url) {
      return previewCreative.url;
    }
    
    // If it's a File object (from upload)
    if (previewCreative instanceof File) {
      return URL.createObjectURL(previewCreative);
    }
    
    // If it's a string URL
    if (typeof previewCreative === 'string') {
      return previewCreative;
    }
    
    return null;
  };

  // Check if the file is a video
  const isVideo = () => {
    if (!previewCreative) return false;
    
    // Check if it's a File object with video type
    if (previewCreative instanceof File) {
      return previewCreative.type.startsWith('video/');
    }
    
    // Check if it's a URL string ending with video extensions
    const src = getImageSrc();
    if (typeof src === 'string') {
      return /\.(mp4|webm|ogg|mov)$/i.test(src);
    }
    
    // Check if object has url property with video extension
    if (previewCreative.url) {
      return /\.(mp4|webm|ogg|mov)$/i.test(previewCreative.url);
    }
    
    return false;
  };

  const imageSrc = getImageSrc();
  const isVideoFile = isVideo();
  
  return (
    <Card className={`border-0 shadow-sm max-w-sm ${sticky ? 'sticky top-4' : ''} ${className}`}>
      <CardContent className="p-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">{title}</h3>
        
        {/* Mock Facebook Ad */}
        <div className="bg-white border rounded-lg overflow-hidden">
          {/* Ad Header */}
          <div className="p-3 border-b">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-yellow-600 flex items-center justify-center text-white font-semibold">
                {campaignName ? campaignName.charAt(0).toUpperCase() : 'C'}
              </div>
              <div className="flex-1">
                <div className="font-semibold text-sm">{campaignName}</div>
                <div className="text-xs text-gray-500">Sponsored</div>
              </div>
            </div>
          </div>

          {/* Primary Text */}
          <div className="p-3 pb-2">
            <div className="text-sm text-gray-900">
              {bodyCopy}
            </div>
          </div>

          {/* Ad Image/Media */}
          <div className="bg-white flex items-center justify-center">
            {imageSrc ? (
              isVideoFile ? (
                <video 
                  src={imageSrc}
                  controls
                  className="w-full h-full object-contain max-h-96"
                  preload="metadata"
                >
                  Your browser does not support the video tag.
                </video>
              ) : (
                <img 
                  src={imageSrc} 
                  alt="Preview" 
                  className="w-full h-full object-contain"
                />
              )
            ) : (
              <div className="text-center text-gray-400 p-8 bg-gray-50">
                <div className="text-4xl font-bold mb-2">
                  {adHeadline}
                </div>
                <div className="text-sm opacity-90">
                  Your creative will appear here
                </div>
              </div>
            )}
          </div>

          {/* Ad Content - Headline and CTA */}
          <div className="flex p-3">
            <div className="flex-1 mr-2">
              {/* Headline */}
              <div className="text-gray-900 font-semibold text-sm mb-1">
                {adHeadline}
              </div>
            </div>
            {/* CTA Button */}
            <button 
              className="w-auto text-gray-800 border border-gray-300 text-sm font-medium py-1 px-3 rounded"
              disabled
            >
              {ctaType}
            </button>
          </div>

          {/* Ad Actions */}
          <div className="px-3 pb-3">
            <div className="flex items-center gap-4 pt-2 border-t">
              <button 
                className="flex items-center gap-1 text-gray-600 hover:text-gray-900 text-sm"
                disabled
              >
                <Heart className="w-4 h-4" />
                <span>Like</span>
              </button>
              <button 
                className="flex items-center gap-1 text-gray-600 hover:text-gray-900 text-sm"
                disabled
              >
                <MessageCircle className="w-4 h-4" />
                <span>Comment</span>
              </button>
              <button 
                className="flex items-center gap-1 text-gray-600 hover:text-gray-900 text-sm"
                disabled
              >
                <Share2 className="w-4 h-4" />
                <span>Share</span>
              </button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default LiveAdPreview;
